import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PostCard from '../../components/PostCard';
import { 
  getAllPosts, 
  getPostsByCategory, 
  searchPosts, 
  getPostsByCategoryStats 
} from '../../data/postsData';
import './index.css';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryStats, setCategoryStats] = useState([]);

  // ====== 新增状态 ======
  const [isFollowing, setIsFollowing] = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [showAuthorTooltip, setShowAuthorTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // 语录列表
  const quotes = [
    { text: "生活不是等待暴风雨过去，而是学会在雨中跳舞。", author: "佚名" },
    { text: "每一天都是新的开始，不要因为昨天而错过今天的美好。", author: "小狗日志" },
    { text: "真正的成长是在平凡的日子里找到不平凡的自己。", author: "咖啡喵" },
    { text: "用心记录生活，用爱温暖他人。", author: "旅行猫" },
    { text: "代码如诗，生活如画，二者都要用心经营。", author: "代码狗" },
    { text: "最好的写作灵感来源于最真实的生活体验。", author: "书虫君" }
  ];

  // 作者信息
  const authorInfo = {
    name: "咖啡喵",
    role: "生活博主 | 最受欢迎作者",
    bio: "热爱生活，喜欢记录生活中的美好瞬间。相信每一天都有值得感恩的小确幸。",
    stats: {
      posts: 4,
      views: 8076,
      likes: 807
    },
    achievements: ["⭐ 最受欢迎作者", "🏆 月度最佳博主", "🎖️ 连续3周热门作者"]
  };

  // ====== 新增函数 ======
  
  // 处理关注作者
  const handleFollowAuthor = () => {
    setIsFollowing(!isFollowing);
    alert(isFollowing ? `已取消关注 ${authorInfo.name}` : `成功关注 ${authorInfo.name}！🐾`);
  };

  // 切换语录
  const handleNextQuote = () => {
    setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
  };

  // 显示作者工具提示
  const handleShowAuthorTooltip = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setShowAuthorTooltip(true);
  };

  // 隐藏作者工具提示
  const handleHideAuthorTooltip = () => {
    setShowAuthorTooltip(false);
  };

  // 从URL参数中获取搜索词
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, [location]);

  // 获取分类统计
  useEffect(() => {
    const stats = getPostsByCategoryStats();
    setCategoryStats([
      { name: '全部', count: getAllPosts().length, icon: '📊' },
      ...stats.map(stat => ({
        name: stat.category,
        count: stat.count,
        icon: getCategoryIcon(stat.category)
      }))
    ]);
  }, []);

  // 获取分类图标
  const getCategoryIcon = (category) => {
    const icons = {
      '生活随笔': '📝',
      '技术分享': '💻',
      '读书笔记': '📚',
      '旅行日记': '✈️',
      '个人成长': '🌱',
      '美食分享': '🍽️'
    };
    return icons[category] || '📄';
  };

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      let filtered = [];
      
      if (searchTerm) {
        // 执行搜索
        filtered = searchPosts(searchTerm);
      } else {
        // 按分类筛选
        filtered = getPostsByCategory(selectedCategory);
      }
      
      setFilteredPosts(filtered);
      setLoading(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm, selectedCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      navigate('/');
    }
  };

  const handleQuickSearch = (term) => {
    setSearchTerm(term);
    navigate(`/?search=${encodeURIComponent(term)}`);
  };

  // 获取统计数据
  const stats = {
    totalPosts: getAllPosts().length,
    totalViews: getAllPosts().reduce((sum, post) => sum + (post.views || 0), 0),
    totalComments: getAllPosts().reduce((sum, post) => sum + (post.comments || 0), 0),
    totalLikes: getAllPosts().reduce((sum, post) => sum + (post.likes || 0), 0),
    featuredPosts: getAllPosts().filter(post => post.featured).length,
  };

  return (
    <div className="home-page fade-in">
      {/* 英雄区域 */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1>
              <span className="gradient-text">Puppy Log</span>
              <span className="paw-print">🐾</span>
            </h1>
            <p className="hero-subtitle">
              已收录 {stats.totalPosts} 篇温暖文章，{stats.totalLikes} 次真诚点赞
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-icon">📝</span>
                <h3>{stats.totalPosts}</h3>
                <p className="stat-label">
                  <span className="stat-label-icon">📄</span>
                  篇温暖文章
                </p>
              </div>
              <div className="stat-item">
                <span className="stat-icon">👁️</span>
                <h3>{stats.totalViews.toLocaleString()}</h3>
                <p className="stat-label">
                  <span className="stat-label-icon">📖</span>
                  次暖心阅读
                </p>
              </div>
              <div className="stat-item">
                <span className="stat-icon">❤️</span>
                <h3>{stats.totalLikes}</h3>
                <p className="stat-label">
                  <span className="stat-label-icon">👍</span>
                  份真诚点赞
                </p>
              </div>
            </div>
          </div>
          
          {/* 右边：新增作者卡片和每日一句 */}
          <div className="hero-right-content">
            {/* 作者卡片 */}
            <div className="author-highlight">
              <h3 className="author-highlight-title">
                <span>👑</span> 本期作者推荐
              </h3>
              
              {/* 作者头像 - 带工具提示 */}
              <div 
                className="author-avatar-large tooltip-trigger"
                onMouseEnter={handleShowAuthorTooltip}
                onMouseLeave={handleHideAuthorTooltip}
                onClick={() => navigate('/about')}
                style={{ cursor: 'pointer' }}
              >
                🐱
              </div>
              
              {/* 作者信息工具提示 */}
              {showAuthorTooltip && (
                <div 
                  className="author-tooltip"
                  style={{
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`
                  }}
                >
                  <div className="tooltip-content">
                    <h4>{authorInfo.name}</h4>
                    <p className="tooltip-bio">{authorInfo.bio}</p>
                    <div className="tooltip-stats">
                      <span>📝 {authorInfo.stats.posts}篇文章</span>
                      <span>👁️ {authorInfo.stats.views.toLocaleString()}阅读</span>
                      <span>❤️ {authorInfo.stats.likes}获赞</span>
                    </div>
                    <div className="tooltip-achievements">
                      {authorInfo.achievements.map((achievement, index) => (
                        <span key={index} className="achievement-badge">
                          {achievement}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="tooltip-arrow"></div>
                </div>
              )}
              
              <h4 className="author-name">{authorInfo.name}</h4>
              <p className="author-role">{authorInfo.role}</p>
              <div className="author-stats-horizontal">
                <div className="author-stat">
                  <span className="author-stat-number">{authorInfo.stats.posts}</span>
                  <span className="author-stat-label">篇文章</span>
                </div>
                <div className="author-stat">
                  <span className="author-stat-number">{authorInfo.stats.views.toLocaleString()}</span>
                  <span className="author-stat-label">阅读量</span>
                </div>
                <div className="author-stat">
                  <span className="author-stat-number">{authorInfo.stats.likes}</span>
                  <span className="author-stat-label">获赞</span>
                </div>
              </div>
              <button 
                className={`follow-author-btn ${isFollowing ? 'following' : ''}`}
                onClick={handleFollowAuthor}
              >
                <span>{isFollowing ? '✅' : '🐾'}</span>
                {isFollowing ? '已关注' : '关注作者'}
              </button>
            </div>
            
            {/* 每日一句 */}
            <div className="daily-quote">
              <div className="quote-header">
                <span className="quote-icon">💬</span>
                <h3>今日小狗语录</h3>
              </div>
              <p className="quote-content">
                {quotes[currentQuoteIndex].text}
              </p>
              <p className="quote-author">—— {quotes[currentQuoteIndex].author}</p>
              <div className="quote-footer">
                <span>🐕 语录 {currentQuoteIndex + 1}/{quotes.length}</span>
                <button 
                  className="new-quote-btn"
                  onClick={handleNextQuote}
                >
                  换一句
                </button>
              </div>
              <div className="quote-decoration">🐶</div>
            </div>
          </div>
        </div>
      </section>

      {/* 搜索区域 */}
      <section className="search-section">
        <div className="search-container">
          <form onSubmit={handleSearch} className="search-box">
            <input
              type="text"
              placeholder={`在 ${stats.totalPosts} 篇文章中搜索...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-btn">
              <span className="search-icon">🔍</span>
              搜索
            </button>
          </form>
          
          {searchTerm && (
            <div className="search-results-info">
              <h3>
                <span className="search-icon">🔍</span>
                搜索 "{searchTerm}" 的结果
                <span className="results-count">（{filteredPosts.length}篇）</span>
              </h3>
              <button 
                className="clear-search-btn"
                onClick={() => {
                  setSearchTerm('');
                  navigate('/');
                }}
              >
                清除搜索
              </button>
            </div>
          )}
          
          <div className="search-tips">
            <span className="tip">热门搜索：</span>
            {['生活', '技术', '旅行', '读书', 'React', '小狗'].map((tag) => (
              <button
                key={tag}
                type="button"
                className="search-tag"
                onClick={() => handleQuickSearch(tag)}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 分类筛选 */}
      <section className="category-section">
        <h2>📁 文章分类</h2>
        <div className="category-filters">
          {categoryStats.map((category) => (
            <button
              key={category.name}
              className={`category-filter ${selectedCategory === category.name ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(category.name);
                if (searchTerm) {
                  setSearchTerm('');
                  navigate('/');
                }
              }}
            >
              <span className="filter-icon">{category.icon}</span>
              <span className="filter-name">{category.name}</span>
              <span className="filter-count">{category.count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* 搜索结果或精选文章 */}
      {searchTerm ? (
        <section className="search-results-section">
          {loading ? (
            <div className="loading-search">
              <div className="searching-dog">🐕</div>
              <p>正在搜索文章...</p>
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="search-results">
              <h3>找到 {filteredPosts.length} 篇相关文章</h3>
              <div className="results-grid">
                {filteredPosts.map((post) => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
            </div>
          ) : (
            <div className="no-search-results">
              <div className="no-results-icon">🔍❌</div>
              <h3>没有找到相关文章</h3>
              <p>尝试使用不同的关键词搜索</p>
              <div className="suggestions">
                <button onClick={() => setSelectedCategory('全部')}>查看所有文章</button>
                <button onClick={() => handleQuickSearch('生活')}>搜索"生活"相关</button>
                <button onClick={() => navigate('/archive')}>浏览归档</button>
              </div>
            </div>
          )}
        </section>
      ) : (
        <section className="featured-section">
          <div className="section-header">
            <h2>
              <span className="star-icon">⭐</span>
              {selectedCategory === '全部' ? '最新文章' : `${selectedCategory}`}
            </h2>
            <button 
              className="btn btn-secondary view-all-btn"
              onClick={() => navigate('/archive')}
            >
              <span>📚</span>
              查看归档
            </button>
          </div>
          
          {loading ? (
            <div className="loading-posts">
              <div className="loading-dog">🐕</div>
              <p>正在加载文章...</p>
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="posts-grid">
              {filteredPosts.slice(0, 6).map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="no-posts">
              <div className="no-posts-icon">📝</div>
              <h3>暂时还没有文章</h3>
              <p>快来发表你的第一篇文章吧！</p>
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/write')}
              >
                ✏️ 写第一篇文章
              </button>
            </div>
          )}
        </section>
      )}

      {/* 写文章入口 */}
      <section className="write-section">
        <div className="write-card">
          <div className="write-content">
            <h3>有想法要分享？</h3>
            <p>记录你的思考，分享你的故事，让更多人看到你的文字。</p>
            <button 
              className="btn btn-primary write-now-btn"
              onClick={() => navigate('/write')}
            >
              <span>✏️</span>
              立即创作
            </button>
          </div>
          <div className="write-decoration">
            <span className="decoration-1">📝</span>
            <span className="decoration-2">✨</span>
            <span className="decoration-3">💭</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;